import {
  SUBSCRIPTION_FREQUENCY,
  SUBSCRIPTION_STATUS,
  REPORT_CATEGORY,
  TIME_UNIT,
  SUBSCRIPTION_TYPE,
  DAYS_IN_WEEK,
  REPORT_NAME_VALIDATION_ERROR_TYPE,
  MTA_CHECKBOX_STATUS,
  MRC_CHECKBOX_STATUS
} from '@urc-ui/utils/types';
import type { TranslateMapConfig } from '@urc-ui/hooks/translate-map';
import { SubscriptionDelivery } from '@urc-ui/store/api/subscriptions/types';
import { MigrationStatus, SubscriptionMigrationStatus } from '@urc-ui/store/api/migration/types';

export const MIGRATION_STATUS_TRANSLATION_MAP: TranslateMapConfig = {
  [MigrationStatus.NOT_STARTED]: {
    id: 'sspa_rcm_migration_status_not_started',
    default: 'Not Started'
  },
  [MigrationStatus.PROCESSING]: {
    id: 'sspa_rcm_migration_status_processing',
    default: 'Processing'
  },
  [MigrationStatus.COMPLETED]: {
    id: 'sspa_rcm_migration_status_completed',
    default: 'Completed'
  },
  [MigrationStatus.FAILED]: {
    id: 'sspa_rcm_migration_status_failed',
    default: 'Failed'
  }
};

export const MIGRATION_STATUS_NOT_MIGRATED_TRANSLATION = {
  id: 'sspa_rcm_migration_status_not_migrated',
  default: 'Not migrated'
};

export interface SubscriptionActionCapabilities {
  readonly canRun: boolean;
  readonly canCopy: boolean;
  readonly canDelete: boolean;
  readonly canUpdateStatus: boolean;
}

export const getSubscriptionActionCapabilities = (migrationStatus?: SubscriptionMigrationStatus): SubscriptionActionCapabilities => {
  const status = migrationStatus?.status;
  const isProcessing = status === MigrationStatus.PROCESSING;
  const isCompleted = status === MigrationStatus.COMPLETED;

  return {
    canRun: !isProcessing && !isCompleted,
    canCopy: !isProcessing,
    canDelete: !isProcessing,
    canUpdateStatus: !isProcessing && !isCompleted
  };
};

export const SUBSCRIPTION_STATUS_MAP: TranslateMapConfig = {
  [SUBSCRIPTION_STATUS.ACTIVE]: {
    id: 'sspa_rcm_ad_reports_subscription_status_active',
    default: 'Scheduled'
  },
  [SUBSCRIPTION_STATUS.PAUSED]: {
    id: 'sspa_rcm_ad_reports_subscription_status_paused',
    default: 'Paused'
  },
  [SUBSCRIPTION_STATUS.INVALID]: {
    id: 'sspa_rcm_ad_reports_subscription_status_invalid',
    default: 'Failed'
  },
  [SUBSCRIPTION_STATUS.FAILED]: {
    id: 'sspa_rcm_ad_reports_subscription_status_invalid',
    default: 'Failed'
  },
  [SUBSCRIPTION_STATUS.EXPIRED]: {
    id: 'sspa_rcm_ad_subscription_status_no_reports_scehduled',
    default: 'Completed'
  }
};

export const SUBSCRIPTION_FREQUENCY_TRANSLATION_MAP: TranslateMapConfig = {
  [SUBSCRIPTION_FREQUENCY.SINGLE]: {
    id: 'sspa_rcm_ad_reports_frequencies_single',
    default: 'One-time'
  },
  [SUBSCRIPTION_FREQUENCY.DAILY]: {
    id: 'sspa_rcm_ad_reports_frequencies_daily',
    default: 'Daily'
  },
  [SUBSCRIPTION_FREQUENCY.WEEKLY]: {
    id: 'sspa_rcm_ad_reports_frequencies_weekly',
    default: 'Weekly'
  },
  [SUBSCRIPTION_FREQUENCY.MONTHLY_DATE]: {
    id: 'sspa_rcm_ad_reports_frequencies_monthly',
    default: 'Monthly'
  }
};

export const SUBSCRIPTION_TYPE_TRANSLATION_MAP: TranslateMapConfig = {
  [SUBSCRIPTION_TYPE.NOW]: {
    id: 'sspa_rcm_ad_reports_request_time_now',
    default: 'Now'
  },
  [SUBSCRIPTION_TYPE.FUTURE]: {
    id: 'sspa_rcm_ad_reports_request_time_future',
    default: 'Future'
  },
  [SUBSCRIPTION_TYPE.RECURRING]: {
    id: 'sspa_rcm_ad_reports_request_time_recurring',
    default: 'Recurring'
  }
};

export const REPORT_CATEGORY_TRANSLATION_MAP: TranslateMapConfig = {
  [REPORT_CATEGORY.SP]: {
    id: 'sspa_rcm_ad_reports_campaign_types_sponsored_products',
    default: 'Sponsored Products'
  },
  [REPORT_CATEGORY.HSA]: {
    id: 'sspa_rcm_ad_reports_campaign_types_sponsored_brands',
    default: 'Sponsored Brands'
  },
  [REPORT_CATEGORY.SBV]: {
    id: 'sspa_rcm_ad_reports_campaign_types_sponsored_brands_video',
    default: 'Sponsored Brands Video'
  },
  [REPORT_CATEGORY.SD]: {
    id: 'sspa_rcm_ad_reports_campaign_types_sponsored_display',
    default: 'Sponsored Display'
  },
  [REPORT_CATEGORY.ST]: {
    id: 'sspa_rcm_ad_reports_campaign_types_sponsored_tv',
    default: 'Sponsored TV'
  },
  [REPORT_CATEGORY.PDA]: {
    id: 'sspa_rcm_ad_reports_report_types_campaign_product_interest',
    default: 'Sponsored Display - Product and Interest'
  },
  // TODO clean this up in https://issues.amazon.com/issues/RCMRX-6170
  [REPORT_CATEGORY.PDA_LEGACY]: {
    id: 'ADS_UCM_PDA_LEGACY_LABEL',
    default: 'Product Display Ads'
  },
  [REPORT_CATEGORY.AA]: {
    id: 'sspa_rcm_ad_reports_campaign_types_amazon_attribution',
    default: 'Amazon Attribution'
  },
  [REPORT_CATEGORY.ADSP]: {
    id: 'sspa_rcm_ad_reports_campaign_types_amazon_dsp',
    default: 'Amazon DSP'
  },
  [REPORT_CATEGORY.ALL]: {
    id: 'sspa_rcm_ad_reports_campaign_types_all_campaigns',
    default: 'All Amazon campaigns'
  }
};

export const REPORT_CATEGORY_GROUPS: REPORT_CATEGORY[][] = [
  [REPORT_CATEGORY.SP, REPORT_CATEGORY.SB, REPORT_CATEGORY.HSA, REPORT_CATEGORY.SBV, REPORT_CATEGORY.SD, REPORT_CATEGORY.ST],
  [REPORT_CATEGORY.ALL],
  [REPORT_CATEGORY.AA],
  [REPORT_CATEGORY.ADSP]
];

export const TIME_UNIT_TRANSLATION_MAP: TranslateMapConfig = {
  [TIME_UNIT.HOUR]: {
    id: 'sspa_rcm_ad_reports_time_units_hourly',
    default: 'Hourly'
  },
  [TIME_UNIT.DAY]: {
    id: 'sspa_rcm_ad_reports_time_units_daily',
    default: 'Daily'
  },
  [TIME_UNIT.WEEK]: {
    id: 'sspa_rcm_ad_reports_time_units_weekly',
    default: 'Weekly'
  },
  [TIME_UNIT.MONTH]: {
    id: 'sspa_rcm_ad_reports_time_units_monthly',
    default: 'Monthly'
  },
  [TIME_UNIT.QUARTER]: {
    id: 'sspa_rcm_ad_reports_time_units_quarterly',
    default: 'Quarterly'
  },
  [TIME_UNIT.SUMMARY]: {
    id: 'sspa_rcm_ad_reports_time_units_summary',
    default: 'Summary'
  }
};

export const DAYS_IN_WEEK_TRANSLATION_MAP: TranslateMapConfig = {
  [DAYS_IN_WEEK.MONDAY]: {
    id: 'sspa_rcm_ad_reports_recurring_weekday_monday',
    default: 'Monday'
  },
  [DAYS_IN_WEEK.TUESDAY]: {
    id: 'sspa_rcm_ad_reports_recurring_weekday_tuesday',
    default: 'Tuesday'
  },
  [DAYS_IN_WEEK.WEDNESDAY]: {
    id: 'sspa_rcm_ad_reports_recurring_weekday_wednesday',
    default: 'Wednesday'
  },
  [DAYS_IN_WEEK.THURSDAY]: {
    id: 'sspa_rcm_ad_reports_recurring_weekday_thursday',
    default: 'Thursday'
  },
  [DAYS_IN_WEEK.FRIDAY]: {
    id: 'sspa_rcm_ad_reports_recurring_weekday_friday',
    default: 'Friday'
  },
  [DAYS_IN_WEEK.SATURDAY]: {
    id: 'sspa_rcm_ad_reports_recurring_weekday_saturday',
    default: 'Saturday'
  },
  [DAYS_IN_WEEK.SUNDAY]: {
    id: 'sspa_rcm_ad_reports_recurring_weekday_sunday',
    default: 'Sunday'
  }
};

export const CREATE_SUBSCRIPTION_LABEL_TRANSLATION_MAP: TranslateMapConfig = {
  [SUBSCRIPTION_TYPE.NOW]: {
    id: 'sspa_rcm_ad_reports_run_report_button',
    default: 'Run report'
  },
  [SUBSCRIPTION_TYPE.FUTURE]: {
    id: 'sspa_rcm_ad_reports_schedule_report_button',
    default: 'Schedule report'
  },
  [SUBSCRIPTION_TYPE.RECURRING]: {
    id: 'sspa_rcm_ad_reports_schedule_report_button',
    default: 'Schedule report'
  }
};

export const FORM_VALIDATION_ERROR_TRANSLATION_MAP: TranslateMapConfig = {
  [REPORT_NAME_VALIDATION_ERROR_TYPE.ERROR_MISSING_REPORT_NAME]: {
    id: 'sspa_rcm_ad_reports_report_name_empty_form_validation_message',
    default: 'Add a report name'
  },
  [REPORT_NAME_VALIDATION_ERROR_TYPE.ERROR_REPORT_NAME_TOO_LONG]: {
    id: 'sspa_rcm_ad_reports_report_name_long_form_validation_message',
    default: 'Delete characters. 64 characters is the maximum.'
  },
  [REPORT_NAME_VALIDATION_ERROR_TYPE.ERROR_INVALID_CHARACTERS]: {
    id: 'sspa_rcm_ad_reports_report_name_invalid_characters_form_validation_message',
    default: 'Report name can’t include: <, >, {{ or }}'
  }
  // [COUNTRY_SELECTOR_VALIDATION_ERROR_TYPE.ERROR_INVALID_SELECTION]: 'Choose a country'
};

export const MTA_CONVERSION_TEXT_TRANSLATION_MAP: TranslateMapConfig = {
  [MTA_CHECKBOX_STATUS.ENABLED]: {
    id: 'sspa_rcm_ad_reports_mta_conversion_checked_text',
    default: 'Last-touch and multi-touch'
  },
  [MTA_CHECKBOX_STATUS.DISABLED]: {
    id: 'sspa_rcm_ad_reports_mta_conversion_unchecked_text',
    default: 'Last-touch'
  }
};

export const determineSubscriptionType = (subscriptionDelivery: SubscriptionDelivery): string => {
  if (subscriptionDelivery.frequency !== SUBSCRIPTION_FREQUENCY.SINGLE) {
    return SUBSCRIPTION_TYPE.RECURRING;
  }

  if (subscriptionDelivery.scheduledEndDate) {
    return SUBSCRIPTION_TYPE.FUTURE;
  }

  return SUBSCRIPTION_TYPE.NOW;
};

export const MRC_STATUS_TEXT_TRANSLATION_MAP: TranslateMapConfig = {
  [MRC_CHECKBOX_STATUS.ENABLED]: {
    id: 'sspa_rcm_ad_reports_mrc_accredited_metrics_selector_checked',
    default: 'Yes'
  },
  [MRC_CHECKBOX_STATUS.DISABLED]: {
    id: 'sspa_rcm_ad_reports_mrc_accredited_metrics_selector_unchecked',
    default: 'No'
  }
};
